import { useState, useEffect } from 'react';
import { Product } from '../../ducks/shop';
import { gettext } from '../../utils/text';
import api from '../../utils/router';
import { getCursorFromUrl } from './utils';

type UseProductsResult = {
    items: Product[];
    loading: boolean;
    error: string | null;
};

export function useProducts(
    categoryId: number | null,
    showId?: number | null,
): UseProductsResult {
    const [items, setItems] = useState<Product[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (categoryId === null) {
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);

            try {
                let cursor: string | null = null;
                const aggregated: Product[] = [];
                const MAX_PAGES = 10;
                let pageCount = 0;

                do {
                    if (cancelled) {
                        return;
                    }

                    if (pageCount >= MAX_PAGES) {
                        console.warn(
                            `Reached max pages (${MAX_PAGES}) for category ${categoryId}`,
                        );
                        break;
                    }

                    const response = await api.products.fetch(undefined, {
                        category_id: categoryId,
                        show_id: showId ?? undefined,
                        cursor: cursor ?? undefined,
                    });
                    aggregated.push(...response.results);
                    cursor = getCursorFromUrl(response.next);
                    pageCount++;
                } while (cursor);

                if (!cancelled) {
                    setItems(aggregated);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) {
                    setItems([]);
                    setError(
                        gettext('Sorry, we could not load this category.'),
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [categoryId, showId]);

    return { items, loading, error };
}
